import { getCookie } from "@insite/client-framework/Common/Cookies";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { Location } from "@insite/client-framework/Components/SpireRouter";
import { updateContext } from "@insite/client-framework/Context";
import { setErrorHandler } from "@insite/client-framework/HandlerCreator";
import Logger from "@insite/client-framework/Logger";
import { calculatePersonas, createSession, Session } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import handleError from "@insite/client-framework/Store/Context/Handlers/HandleError";
import loadCurrentWebsite from "@insite/client-framework/Store/Context/Handlers/LoadCurrentWebsite";
import loadSession from "@insite/client-framework/Store/Context/Handlers/LoadSession";
import loadSettings from "@insite/client-framework/Store/Context/Handlers/LoadSettings";
import setPunchOutSessionId from "@insite/client-framework/Store/Context/Handlers/SetPunchOutSessionId";
import setShowInventory, {
    ShowInventoryCookieName,
} from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing, { ShowPricingCookieName } from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import { loadPage, loadPageByType } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import * as React from "react";
import { PropsWithChildren } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    location: Location;
}

const mapStateToProps = (state: ApplicationState) => ({
    isWebsiteLoaded: state.context.isWebsiteLoaded,
    isWebsiteLoading: state.context.isWebsiteLoading,
    isSessionLoaded: state.context.isSessionLoaded,
    isSessionLoading: state.context.isSessionLoading,
    areSettingsLoaded: state.context.areSettingsLoaded,
    areSettingsLoading: state.context.areSettingsLoading,
    session: state.context.session,
    settings: getSettingsCollection(state),
});

const setLocation = (location: Location): AnyAction => ({
    type: "Data/Pages/SetLocation",
    location,
});

const mapDispatchToProps = {
    loadCurrentWebsite,
    loadSession,
    loadSettings,
    setLocation,
    setPunchOutSessionId,
    setShowInventory,
    setShowPricing,
    loadPage,
    loadPageByType,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

class SessionLoader extends React.Component<PropsWithChildren<Props>> {
    private checkedPersonas = false;

    UNSAFE_componentWillMount() {
        setErrorHandler(handleError);

        const props = this.props;

        const parsedQuery = parseQueryString<{
            setcontextlanguagecode: string;
            setcontextcurrencycode: string;
            access_token: string;
        }>(props.location.search);

        props.setLocation(props.location);

        if (!props.isWebsiteLoaded && !props.isWebsiteLoading) {
            props.loadCurrentWebsite();
        }

        if (!props.isSessionLoaded && !props.isSessionLoading) {
            props.loadSession({
                setContextLanguageCode: parsedQuery.setcontextlanguagecode,
                setContextCurrencyCode: parsedQuery.setcontextcurrencycode,
                onComplete: ({ apiResult }: { apiResult: Session }) => {
                    if (apiResult && parsedQuery.access_token) {
                        createSession({
                            userName: apiResult.userName,
                            accessToken: parsedQuery.access_token,
                        });
                    }
                },
            });
        }

        if (!props.areSettingsLoaded && !props.areSettingsLoading) {
            props.loadSettings();
        }

        this.checkDeferredPersonas();
    }

    componentDidMount() {
        const punchOutSessionId = getCookie("PunchOutSessionId");
        if (punchOutSessionId) {
            this.props.setPunchOutSessionId({ punchOutSessionId });
        }
    }

    componentDidUpdate() {
        this.checkDeferredPersonas();
        const { isSessionLoading, isSessionLoaded, areSettingsLoaded, session, settings } = this.props;
        if (!isSessionLoading && isSessionLoaded && areSettingsLoaded && session && settings) {
            updateContext({
                ...session,
                settings,
                pickUpWarehouseId: session.pickUpWarehouse ? session.pickUpWarehouse.id : undefined,
            });
        }

        if (isSessionLoaded && session) {
            this.props.setShowInventory({
                showInventory:
                    !session.displayPricingAndInventory ||
                    getCookie(ShowInventoryCookieName)?.toLowerCase() !== "false",
            });
            this.props.setShowPricing({
                showPricing:
                    !session.displayPricingAndInventory || getCookie(ShowPricingCookieName)?.toLowerCase() !== "false",
            });
        }
    }

    private async checkDeferredPersonas() {
        if (IS_SERVER_SIDE || this.checkedPersonas) {
            return;
        }

        const { session, settings, areSettingsLoaded } = this.props;

        // Wait until the session and settings have loaded before running the deferred
        // persona check. Otherwise the one-shot guard below would be consumed on an early
        // render and the calculation would never run once the data becomes available.
        if (!session?.personas || !areSettingsLoaded) {
            return;
        }

        this.checkedPersonas = true;

        const personaCookie = getCookie("SetContextPersonaIds");
        if (personaCookie || !settings.websiteSettings.calculateCustomerSegmentAfterLoadingPage) {
            return;
        }

        try {
            const calculatedPersonas = await calculatePersonas();

            // this should only be the default persona, we don't calculate personas anymore unless calculatePersonas is explicitly called
            const currentIds = session.personas
                .map(p => p.id)
                .sort()
                .join(",");
            const calculatedIds = calculatedPersonas
                .map(p => p.id)
                .sort()
                .join(",");

            if (currentIds !== calculatedIds) {
                // if the server calculated different personas, we need to get the whole site reloaded
                // in case the content is different by persona.
                this.props.loadSession({
                    isSessionUpdate: true,
                    onComplete: () => {
                        this.props.loadPageByType("Header");
                        this.props.loadPageByType("Footer");
                        this.props.loadPage(this.props.location, undefined, undefined, true);
                    },
                });
            }
        } catch (error) {
            Logger.error("Deferred persona calculation failed.", error);
        }
    }

    render() {
        const { isSessionLoaded, isWebsiteLoaded, areSettingsLoaded } = this.props;
        if (!isSessionLoaded || !isWebsiteLoaded || !areSettingsLoaded) {
            return null;
        }

        return this.props.children;
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(SessionLoader);
